import { useTranslation } from 'next-i18next'
import { Fragment, useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import {
  resetCorrectConsecutiveAnswersActionCreator,
  selectCurrentRound,
  setAnswerStateActionCreator,
} from '~/bootcamp/slices/bootcamp-activity.slice'
import { BackToCurriculumPage, NextLevelText } from '~/bootcamp/pages/BootcampView/BootcampContent/NavigationButton'
import { selectNextBootcampPage, setNextBootcampPageActionCreator } from '~/gamification/slices/gamification.slice'
import Button from '~/components/Buttons/Button'
import { useGetActivitySetAccuracyQuery, useGetBestAccuracyQuery } from '~/bootcamp/apis/get-accuracy.api'
import Loader from '~/components/Utility/Loader'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import { GRADE_QUERY_PARAM } from '~/bootcamp/constants/bootcamp.const'
import useNextActivityType from '~/bootcamp/hooks/useNextActivityType'
import SetCompletedScore from '~/bootcamp/pages/BootcampView/BootcampContent/SetCompletedScore'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SetCompletedAvatar from '~/bootcamp/pages/BootcampView/BootcampContent/SetCompletedAvatar'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import TimesBoldIcon from '~/components/Icons/svgs/TimesBoldIcon'
import useShowRoundSummary from '~/bootcamp/hooks/useShowRoundSummary'

const MODAL_ID = 'chests-modal'
const initialState = { currentActivityType: 0 }

export default function ModalChestsCounter(): React.ReactElement {
  const router = useRouter()
  const { asPath } = router
  const { t } = useTranslation('bootcamp')
  const dispatch = useAppDispatch()
  const rewardsCount = useAppSelector(selectNextBootcampPage)
  const currentRound = useAppSelector(selectCurrentRound)
  const showRoundSummary = useShowRoundSummary()

  const hideModal = !rewardsCount || rewardsCount.currentActivityType === 0
  const asPathWithoutQuery = asPath.split('?')[0]
  const { data: bestData } = useGetBestAccuracyQuery({ path: asPathWithoutQuery })
  const { data, isLoading } = useGetActivitySetAccuracyQuery(
    { path: asPathWithoutQuery, retakeRound: currentRound },
    { skip: hideModal },
  )
  const isGradePage = useIsBootcampGradePage()
  const nextActivityType = useNextActivityType()

  const onClick = (event: any): void => {
    if (document.getElementById(MODAL_ID)?.contains(event.target)) return
    dispatch(setNextBootcampPageActionCreator(initialState))
  }

  const changeActivityType = useCallback((): void => {
    if (nextActivityType > 0) {
      const urlParts = asPath.split('/')
      urlParts[urlParts.length - 1] = nextActivityType.toString()
      const nextActivityTypeQueryParam = isGradePage
        ? {
            [GRADE_QUERY_PARAM]: isGradePage,
          }
        : {}

      router.replace(
        {
          pathname: urlParts.join('/'),
          query: nextActivityTypeQueryParam,
        },
        undefined,
        { shallow: true, scroll: false },
      )

      dispatch(resetCorrectConsecutiveAnswersActionCreator())
      dispatch(setAnswerStateActionCreator(null)) // Reset answer state, null means not attempted.
    }
  }, [asPath, dispatch, isGradePage, nextActivityType, router])

  const accuracy = data && data.accuracy > 0 ? data.accuracy : bestData ? bestData.accuracy : 0
  const className = `!w-full border-2 text-white hover:scale-110 duration-300 bg-purple border-purple`

  const nextButton = useMemo(() => {
    if (nextActivityType > 0) {
      return (
        <Button className={className} onClick={() => changeActivityType()} size="medium">
          {`${t('goToActivitySet')} ${nextActivityType}`}
        </Button>
      )
    } else if (isGradePage) {
      return <BackToCurriculumPage className={className} />
    } else {
      return <NextLevelText className={className} />
    }
  }, [changeActivityType, className, isGradePage, nextActivityType, t])

  if (hideModal || !showRoundSummary || accuracy < 0 || nextActivityType > 0) {
    return <Fragment />
  }

  return (
    <aside
      className="fixed inset-0 z-modal bg-black/70 backdrop-filter backdrop-blur-sm"
      onClick={onClick}
      title="Activity set completed modal"
    >
      <section className="h-full flex items-center justify-center px-4 md:px-8">
        <div className="relative bg-purple-darker text-white w-full max-w-150 rounded flex flex-col shadow-large">
          <button
            aria-label={t('generics:ariaLabelButtons.closeWindow')}
            className="absolute right-4 top-4 hover:scale-110 duration-300 cursor-pointer z-10"
            onClick={onClick}
          >
            <SVGIcon icon={TimesBoldIcon} pathClassName="text-white" size={24} />
          </button>
          <Loader className="mx-auto h-80" isLoading={isLoading}>
            <div className="relative w-full h-full overflow-hidden">
              <div className="absolute flex justify-center left-0 right-0 top-10 md:top-20 mx-auto -mt-1 scale-75 z-10">
                <SetCompletedAvatar isEndOfLevel />
                <div className="absolute z-20 bottom-4">
                  <h3 className="font-semibold leading-6 text-xl ml-2 uppercase">{t('levelCompleted')}</h3>
                </div>
              </div>
              <ResponsiveImage
                className="relative block overflow-hidden w-full h-96 md:h-125 -mb-12"
                fit="cover"
                height={500}
                imageClassName="absolute top-8 md:top-0 left-1/2 right-1/2 bottom-0 min-w-[42rem] -translate-x-1/2 md:scale-110 m-auto"
                imageWidths="600"
                srcKey="bootcamp/completed-set-bg.png"
                width={600}
              />

              <SetCompletedScore correctPercent={accuracy} isEndOfLevel nextButton={nextButton} />
            </div>
          </Loader>
        </div>
      </section>
    </aside>
  )
}
